/**
 * TherapyInbox — Daily batch review of draft therapy notes.
 *
 * End-of-day workflow:
 * 1. All draft notes listed as cards
 * 2. Swipe/tap to approve each one
 * 3. "Approve All" for days where everything looks correct
 * 4. Edit button on each card for corrections
 * 5. Unapproved notes highlighted with gentle reminder
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, Container, Divider,
  IconButton, Paper, Stack, TextField, Tooltip, Typography, Alert,
  CircularProgress, Collapse, Fade, Badge,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  CheckCircleOutline as ApproveOutlineIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Inbox as InboxIcon,
  ArrowBack as BackIcon,
  DoneAll as ApproveAllIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
  Replay as RetryIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { EASI_DESIGN } from '../../theme/design-tokens';
import { therapy, type TherapyNote } from '../../api/endpoints';

const d = EASI_DESIGN;

export default function TherapyInbox() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<TherapyNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNote, setExpandedNote] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [approving, setApproving] = useState<string | null>(null);
  const [batchApproving, setBatchApproving] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await therapy.getInbox(50);
      // client.ts unwrapResponse() already extracts the inner data from the
      // API envelope, so res.data IS the array — not res.data.data.
      // Backend returns snake_case from DynamoDB; normalize to camelCase here
      // so the rest of the component can use typed field access consistently.
      const rawData: any[] = Array.isArray(res?.data) ? (res.data as any) : [];
      const normalized = rawData.map((n: any) => ({
        ...n,
        patientId: n.patientId || n.patient_id || '',
        sessionTimestamp: n.sessionTimestamp || n.session_timestamp || '',
        approvalStatus: n.approvalStatus || n.approval_status || 'draft',
        inputMode: n.inputMode || n.input_mode || 'ambient',
        durationMinutes: n.durationMinutes ?? n.duration_minutes ?? 0,
        serviceType: n.serviceType || n.service_type || '',
        rawDraft: n.rawDraft || n.raw_draft || '',
        approvedNote: n.approvedNote || n.approved_note || '',
        soapNote: n.soapNote || n.soap_note || null,
        goalsTargeted: n.goalsTargeted || n.goals_targeted || [],
        processingStatus: n.processingStatus ?? n.processing_status ?? '',
        processingError: n.processingError ?? n.processing_error ?? '',
      }));
      setDrafts(normalized);
    } catch (err: any) {
      setError(err.message || 'Failed to load inbox.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const handleApprove = useCallback(async (note: TherapyNote, approvedText?: string) => {
    const key = `${note.patientId}:${note.sessionTimestamp}`;
    setApproving(key);
    try {
      await therapy.approveNote(note.patientId, note.sessionTimestamp, approvedText);
      setDrafts(prev => prev.filter(n => `${n.patientId}:${n.sessionTimestamp}` !== key));
      setEditingNote(null);
    } catch (err: any) {
      setError(err.message || 'Failed to approve note.');
    }
    setApproving(null);
  }, []);

  const handleRetry = useCallback(async (note: TherapyNote) => {
    const key = `${note.patientId}:${note.sessionTimestamp}`;
    setRetrying(key);
    try {
      await therapy.retryNote(note.patientId, note.sessionTimestamp);
      // Optimistically flip to processing so UI updates; refresh shortly after.
      setDrafts(prev => prev.map(n =>
        `${n.patientId}:${n.sessionTimestamp}` === key
          ? { ...n, processingStatus: 'processing' as const, processingError: '' }
          : n
      ));
      setTimeout(() => { loadDrafts(); }, 3000);
    } catch (err: any) {
      setError(err.message || 'Retry failed.');
    }
    setRetrying(null);
  }, [loadDrafts]);

  const handleDelete = useCallback(async (note: TherapyNote) => {
    const key = `${note.patientId}:${note.sessionTimestamp}`;
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setDeleting(key);
    try {
      await therapy.deleteNote(note.patientId, note.sessionTimestamp);
      setDrafts(prev => prev.filter(n => `${n.patientId}:${n.sessionTimestamp}` !== key));
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
    }
    setDeleting(null);
  }, []);

  // Notes whose processing_status is "processing" for >10 minutes are almost
  // certainly failed — the async Lambda should have completed or errored long
  // before then. Treat these as effectively failed so the clinician gets Retry
  // and Delete affordances instead of a spinner that never finishes.
  const PROCESSING_STALE_MS = 10 * 60 * 1000;
  const isStaleProcessing = (note: TherapyNote): boolean => {
    const ps = (note as any).processingStatus ?? (note as any).processing_status;
    if (ps !== 'processing') return false;
    const ts = note.sessionTimestamp;
    if (!ts) return false;
    const started = new Date(ts).getTime();
    if (Number.isNaN(started)) return false;
    return Date.now() - started > PROCESSING_STALE_MS;
  };
  const effectiveStatus = (note: TherapyNote): string => {
    const ps = (note as any).processingStatus ?? (note as any).processing_status;
    if (ps === 'processing' && isStaleProcessing(note)) return 'failed';
    return ps || '';
  };

  // Only notes that have finished processing (not stuck in processing or failed) can be approved.
  const isApprovable = (note: TherapyNote) => {
    const ps = effectiveStatus(note);
    return ps !== 'processing' && ps !== 'failed';
  };

  const handleBatchApprove = useCallback(async () => {
    setBatchApproving(true);
    try {
      const approvable = drafts.filter(isApprovable);
      if (approvable.length === 0) return;
      const notes = approvable.map(n => ({
        patientId: n.patientId,
        sessionTimestamp: n.sessionTimestamp,
      }));
      await therapy.batchApprove(notes);
      // Remove only the approved notes; keep processing/failed in the list.
      const approvedKeys = new Set(approvable.map(n => `${n.patientId}:${n.sessionTimestamp}`));
      setDrafts(prev => prev.filter(n => !approvedKeys.has(`${n.patientId}:${n.sessionTimestamp}`)));
    } catch (err: any) {
      setError(err.message || 'Batch approve failed.');
    }
    setBatchApproving(false);
  }, [drafts]);

  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch { return ts; }
  };

  const getNotePreview = (note: TherapyNote) => {
    return note.approvedNote || note.rawDraft || note.soapNote?.objective || 'No content yet — processing may still be in progress.';
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={800} color={d.color.emeraldDarkest}>
              Notes Inbox
            </Typography>
            <Badge
              badgeContent={drafts.length}
              color="warning"
              sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}
            >
              <InboxIcon sx={{ color: d.color.emerald }} />
            </Badge>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Review and approve your session notes
          </Typography>
        </Box>
        <IconButton onClick={loadDrafts} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: d.color.emerald }} />
        </Box>
      )}

      {/* Empty state */}
      {!loading && drafts.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px solid ${d.color.emeraldBorder}`,
            borderRadius: 3,
            bgcolor: d.color.emeraldBg,
          }}
        >
          <ApproveIcon sx={{ fontSize: 56, color: d.color.emerald, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} color={d.color.emeraldDarkest}>
            All caught up!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No draft notes awaiting review.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            Back to Caseload
          </Button>
        </Paper>
      )}

      {/* Approve All button — only for notes that are ready to review */}
      {!loading && drafts.filter(isApprovable).length > 1 && (
        <Button
          fullWidth
          variant="contained"
          startIcon={batchApproving ? <CircularProgress size={20} color="inherit" /> : <ApproveAllIcon />}
          onClick={handleBatchApprove}
          disabled={batchApproving}
          sx={{
            mb: 2,
            py: 1.5,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: d.color.emerald,
            borderRadius: 2,
            '&:hover': { bgcolor: d.color.emeraldDark },
          }}
        >
          Approve All ({drafts.filter(isApprovable).length} notes)
        </Button>
      )}

      {/* Draft note cards */}
      <Stack spacing={2}>
        {drafts.map(note => {
          const key = `${note.patientId}:${note.sessionTimestamp}`;
          const isExpanded = expandedNote === key;
          const isEditing = editingNote === key;
          const isApproving = approving === key;
          const isRetrying = retrying === key;
          const isDeleting = deleting === key;
          const procStatus = effectiveStatus(note);
          const isStale = isStaleProcessing(note);
          const procError = isStale
            ? 'Processing did not complete. You can retry or delete this note.'
            : ((note as any).processingError ?? (note as any).processing_error);
          const isFailed = procStatus === 'failed';
          const isProcessing = procStatus === 'processing' && !isStale;

          return (
            <Card
              key={key}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: note.approvalStatus === 'needs_review' ? '#F59E0B' : d.color.slate200,
                borderWidth: note.approvalStatus === 'needs_review' ? 2 : 1,
                transition: 'all 200ms ease-out',
                '&:hover': {
                  borderColor: d.color.emerald,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                {/* Header row */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body1" fontWeight={700}>
                      {formatTimestamp(note.sessionTimestamp)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      <Chip
                        label={note.inputMode === 'ambient' ? 'Recorded' : note.inputMode === 'dictation' ? 'Dictated' : 'Manual'}
                        size="small"
                        sx={{
                          bgcolor: note.inputMode === 'ambient' ? d.color.emeraldPale : d.color.bluePale,
                          color: note.inputMode === 'ambient' ? d.color.emeraldDarkest : d.color.blueDeep,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      {note.durationMinutes > 0 && (
                        <Chip label={`${note.durationMinutes} min`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {note.serviceType && (
                        <Chip label={note.serviceType.replace('_', ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {/* Processing status indicator */}
                      {procStatus === 'processing' && (
                        <Chip
                          icon={<CircularProgress size={12} sx={{ color: '#F59E0B' }} />}
                          label="Processing"
                          size="small"
                          sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                      {procStatus === 'failed' && (
                        <Chip
                          icon={<ErrorIcon sx={{ fontSize: '14px !important', color: '#DC2626' }} />}
                          label="Failed"
                          size="small"
                          sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                      {procStatus === 'completed' && (
                        <Chip
                          icon={<ApproveIcon sx={{ fontSize: '14px !important', color: '#059669' }} />}
                          label="Ready"
                          size="small"
                          sx={{ bgcolor: d.color.emeraldPale, color: d.color.emeraldDarkest, fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    {isFailed ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleRetry(note)}
                          disabled={isRetrying}
                          title="Retry processing"
                          sx={{ color: '#2563EB', '&:hover': { bgcolor: '#EFF6FF' } }}
                        >
                          {isRetrying ? <CircularProgress size={20} /> : <RetryIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(note)}
                          disabled={isDeleting}
                          title="Delete note"
                          sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                        >
                          {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                        </IconButton>
                      </>
                    ) : isProcessing ? (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(note)}
                        disabled={isDeleting}
                        title="Cancel and delete"
                        sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                      >
                        {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                      </IconButton>
                    ) : (
                      <>
                        <Tooltip title="Edit note">
                          <IconButton
                            size="small"
                            onClick={() => { setEditingNote(isEditing ? null : key); setEditText(getNotePreview(note)); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete note">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(note)}
                            disabled={isDeleting}
                            sx={{ color: d.color.slate400, '&:hover': { color: '#DC2626', bgcolor: '#FEE2E2' } }}
                          >
                            {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={isApproving ? <CircularProgress size={14} color="inherit" /> : <ApproveOutlineIcon />}
                          onClick={() => handleApprove(note)}
                          disabled={isApproving}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1.5,
                            bgcolor: d.color.emerald,
                            '&:hover': { bgcolor: d.color.emeraldDark },
                          }}
                        >
                          {isApproving ? 'Approving…' : 'Approve'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>

                {/* Goals targeted */}
                {note.goalsTargeted && note.goalsTargeted.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {note.goalsTargeted.map((g: any, i: number) => (
                      <Chip
                        key={i}
                        label={`${g.goalText?.slice(0, 30) || 'Goal'}${g.accuracyPct != null ? ` ${g.accuracyPct}%` : ''}`}
                        size="small"
                        sx={{ bgcolor: '#F3F4F6', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                )}

                {/* Failure message — shown in red so the clinician sees why */}
                {isFailed && procError && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1.5, color: '#991B1B', fontStyle: 'italic' }}
                  >
                    {procError}
                  </Typography>
                )}

                {/* Preview / Expand */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 999 : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedNote(isExpanded ? null : key)}
                >
                  {getNotePreview(note)}
                </Typography>

                {/* SOAP note details (expanded) */}
                <Collapse in={isExpanded}>
                  {note.soapNote && (note.soapNote.subjective || note.soapNote.objective) && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      {['subjective', 'objective', 'assessment', 'plan'].map(section => {
                        const content = (note.soapNote as any)?.[section];
                        if (!content) return null;
                        return (
                          <Box key={section} sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} color={d.color.emeraldDark} sx={{ textTransform: 'uppercase' }}>
                              {section}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {content}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Collapse>

                {/* Toggle expand */}
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <IconButton size="small" onClick={() => setExpandedNote(isExpanded ? null : key)}>
                    {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                  </IconButton>
                </Box>

                {/* Edit mode */}
                <Collapse in={isEditing}>
                  <Divider sx={{ my: 1.5 }} />
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={() => setEditingNote(null)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleApprove(note, editText)}
                      disabled={isApproving}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: d.color.emerald,
                        '&:hover': { bgcolor: d.color.emeraldDark },
                      }}
                    >
                      Save & Approve
                    </Button>
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Container>
  );
}
